import {useState} from "react";
import Container from "@components/layout/Container";
import {sendContactForm} from "@/services/forms.service";
import {useSiteSettings} from "@/context/SiteSettingsContext";
import {phoneHref} from "@/utils/contactHelpers";
import PhoneInput from "@components/forms/PhoneInput";
import {validatePhone} from "@/utils/validatePhone";

const IconPhone = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
		<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
	</svg>
);

const IconEmail = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
		<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
		<path d="M22 6l-10 7L2 6" />
	</svg>
);

const IconLocation = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
		<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
		<circle cx="12" cy="10" r="3" />
	</svg>
);

export default function ContactSection() {
	const [status, setStatus] = useState("idle");
	const [phone, setPhone] = useState("");
	const [phoneError, setPhoneError] = useState("");
	const {phones, email, address} = useSiteSettings()

	const contactInfo = [
		{icon: <IconPhone />, label: "телефон", phones},
		{icon: <IconEmail />, label: "email", value: email, href: `mailto:${email}`},
		{icon: <IconLocation />, label: "локація", value: address},
	];

	async function handleSubmit(e) {
		e.preventDefault();
		const err = phone ? validatePhone(phone) : "Введіть номер телефону";
		if (err) {
			setPhoneError(err);
			return;
		}
		setPhoneError("");
		setStatus("loading");
		const fd = new FormData(e.target);
		try {
			await sendContactForm({
				name: fd.get("name"),
				phone,
				message: fd.get("message"),
			});
			setStatus("success");
			e.target.reset();
			setPhone("");
			setTimeout(() => setStatus("idle"), 4000);
		} catch {
			setStatus("error");
			setTimeout(() => setStatus("idle"), 4000);
		}
	}

	return (
		<section className="contact" id="contact">
			<Container>
				<div className="contact-grid">
					<div className="contact-left">
						<span className="eyebrow eyebrow--gold contact-eyebrow">
							Зв&apos;яжіться з нами
						</span>
						<h2 className="h2-on-dark-serif contact-title">
							Ми поруч, <span style={{color: "rgba(102, 136, 170, 1)", fontStyle: "italic"}}>коли потрібно</span>
						</h2>
						<p className="contact-text">
							Розуміємо, що звернутись у таку хвилину непросто. Ми відповімо з
							повагою і терпінням, без поспіху та тиску.
						</p>

						<div className="contact-list">
							{contactInfo.map((c) => (
								<div key={c.label} className="contact-item">
									<span className="contact-icon">{c.icon}</span>
									<div className="contact-meta">
										<span className="contact-label">{c.label}</span>
										{c.phones ? (
											<div className="contact-value">
												{c.phones.map((p) => (
													<a key={p} href={phoneHref(p)} className="contact-value-link">
														{p}
													</a>
												))}
											</div>
										) : c.href ? (
											<a href={c.href} className="contact-value">
												{c.value}
											</a>
										) : (
											<span className="contact-value">{c.value}</span>
										)}
									</div>
								</div>
							))}
						</div>
					</div>

					<form onSubmit={handleSubmit} className="contact-form">
						<div className="form-row">
							<div className="form-field">
								<label className="form-label" htmlFor="contact-name">
									Ім&apos;я та прізвище
								</label>
								<input
									id="contact-name"
									name="name"
									type="text"
									className="form-input"
									placeholder="Іван Петренко"
									required
								/>
							</div>
							<div className="form-field">
								<label className="form-label" htmlFor="contact-phone">
									Телефон
								</label>
								<PhoneInput
									id="contact-phone"
									value={phone}
									onChange={(v) => {
										const next = v ?? "";
										setPhone(next);
										setPhoneError(validatePhone(next));
									}}
									className={phoneError ? "is-invalid" : ""}
									aria-invalid={Boolean(phoneError)}
								/>
								{phoneError && <span className="field-error">{phoneError}</span>}
							</div>
						</div>

						<div className="form-field">
							<label className="form-label" htmlFor="contact-message">
								Повідомлення
							</label>
							<textarea
								id="contact-message"
								name="message"
								className="form-textarea"
								placeholder="Розкажіть про ваш запит, побажання та терміни..."
							/>
						</div>

						<button
							type="submit"
							className="form-submit"
							disabled={status === "loading" || status === "success"}
						>
							{status === "loading" && "Відправляємо..."}
							{status === "success" && "✓ Заявку відправлено"}
							{status === "error" && "Помилка. Спробуйте ще раз"}
							{status === "idle" && "Надіслати заявку"}
						</button>
					</form>
				</div>
			</Container>
		</section>
	);
}
